import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  cx,
  Badge,
  Modal,
  Field,
  Input,
  Button,
  buttonClass,
  LinkButton,
  useToast,
} from "@/components/ui";
import { AUTO_REFRESH_MS, DataFreshness, freshnessOf } from "@/components/ui/DataFreshness";
import {
  domainKeys,
  domainsApi,
  healthPill,
  longDate,
  relativeDays,
  type Domain,
  type DomainList,
  type ExternalDomain,
  type Folder,
} from "@/lib/domains";
import { DomainIcon } from "@/components/domains/parts";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  ["", "All"],
  ["active", "Active"],
  ["expiring_soon", "Expiring soon"],
  ["renewal_failed", "Renewal failed"],
  ["expired", "Expired"],
  ["registering", "Registering"],
  ["transferring_in", "Transferring in"],
  ["transferring_out", "Transferring out"],
] as const;
const EXPIRES_OPTIONS = [
  ["", "Any time"],
  ["30d", "Next 30 days"],
  ["90d", "Next 90 days"],
  ["365d", "Next year"],
  ["expired", "Already expired"],
] as const;
const AUTO_RENEW_OPTIONS = [
  ["", "Any"],
  ["true", "On"],
  ["false", "Off"],
] as const;
const CONNECTED_OPTIONS = [
  ["", "Any"],
  ["website", "Website"],
  ["nothing", "Nothing"],
] as const;
const SORT_OPTIONS = [
  ["expiring", "Expiring soon"],
  ["name", "Name"],
  ["newest", "Newest"],
] as const;

/** Figma "Domains" (4724-8346): folders on the left, every domain the workspace owns on the right. */
export function DomainsPage() {
  const { orgId, can } = useOrganization();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const page = Number(params.get("page") ?? "1") || 1;
  const filters = {
    q: params.get("q") ?? undefined,
    status: params.get("status") ?? undefined,
    expires: params.get("expires") ?? undefined,
    folder: params.get("folder") ?? undefined,
    auto_renew: params.get("auto_renew") ?? undefined,
    connected: params.get("connected") ?? undefined,
    sort: params.get("sort") ?? "expiring",
    page,
    page_size: PAGE_SIZE,
  };
  const list = useQuery({
    queryKey: domainKeys.list(orgId, filters),
    queryFn: () => domainsApi.list(orgId, filters),
    placeholderData: keepPreviousData,
    refetchInterval: AUTO_REFRESH_MS,
  });

  // Typing searches after a pause (the URL keeps the filter, so back/forward and links work).
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get("q") ?? "") !== q.trim()) set({ q: q.trim() || null, page: null });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  function set(patch: Record<string, string | null>) {
    setParams(
      (p) => {
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "") p.delete(k);
          else p.set(k, v);
        }
        return p;
      },
      { replace: true },
    );
  }

  if (list.isPending) return <FullPageSpinner />;
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const data = list.data;
  const folderCount = data.folders.length;
  const filtered = Boolean(
    filters.q ||
    filters.status ||
    filters.expires ||
    filters.folder ||
    filters.auto_renew ||
    filters.connected,
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-2xl font-bold text-fg">Domains</h1>
          <p className="text-sm text-fg-subtle">
            {data.all_count} {data.all_count === 1 ? "domain" : "domains"}
            {folderCount ? ` in ${folderCount} ${folderCount === 1 ? "folder" : "folders"}` : ""}
            {data.external.length ? ` · ${data.external.length} connected from elsewhere` : ""}
          </p>
          <DataFreshness {...freshnessOf(list)} />
        </div>
        <Link to="/domains/contacts" className="text-[13px] font-semibold text-fg-muted hover:text-fg">
          Contacts
        </Link>
        <Link to="/domains/orders" className="text-[13px] font-semibold text-fg-muted hover:text-fg">
          Order history
        </Link>
        <LinkButton
          to="/domains/transfer/bulk"
          className="text-[13px] font-semibold text-fg-muted hover:text-fg"
          requires="domains:manage"
        >
          Transfer domains
        </LinkButton>
        <LinkButton to="/domains/find" className={buttonClass("brand", "pill")} requires="domains:manage">
          + Find a domain
        </LinkButton>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <label className="flex w-full items-center gap-2 rounded-[9px] border border-border bg-surface px-3 py-[9px] sm:w-[200px]">
          <DomainIcon name="search" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search domains..."
            aria-label="Search domains"
            className="min-w-0 flex-1 bg-transparent text-[12.5px] text-fg outline-none placeholder:text-fg-subtle"
          />
        </label>
        <FilterSelect
          label="Status"
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(v) => set({ status: v, page: null })}
        />
        <FilterSelect
          label="Expires"
          value={filters.expires}
          options={EXPIRES_OPTIONS}
          onChange={(v) => set({ expires: v, page: null })}
        />
        <FilterSelect
          label="Folder"
          value={filters.folder}
          options={[["", "All"], ["unfiled", "Unfiled"], ...data.folders.map((f) => [f.id, f.name] as const)]}
          onChange={(v) => set({ folder: v, page: null })}
        />
        <FilterSelect
          label="Auto-renew"
          value={filters.auto_renew}
          options={AUTO_RENEW_OPTIONS}
          onChange={(v) => set({ auto_renew: v, page: null })}
        />
        <FilterSelect
          label="Connected to"
          value={filters.connected}
          options={CONNECTED_OPTIONS}
          onChange={(v) => set({ connected: v, page: null })}
        />
        <span className="hidden flex-1 lg:block" />
        <FilterSelect
          label="Sort"
          value={filters.sort}
          options={SORT_OPTIONS}
          onChange={(v) => set({ sort: v || "expiring" })}
        />
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <Folders
          data={data}
          active={filters.folder ?? ""}
          onPick={(folder) => set({ folder: folder || null, page: null })}
          canManage={can("domains:manage")}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-3.5">
          {data.domains.length === 0 ? (
            <EmptyList filtered={filtered} hasExternal={data.external.length > 0} />
          ) : (
            <DomainTable domains={data.domains} folders={data.folders} />
          )}
          {data.total > 0 && (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={data.total}
              onPage={(p) => set({ page: p === 1 ? null : String(p) })}
            />
          )}
          {data.external.length > 0 && !filtered && <ExternalTable rows={data.external} />}
        </div>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: readonly (readonly [string, string])[];
  onChange: (value: string) => void;
}) {
  const current = options.find(([v]) => v === (value ?? "")) ?? options[0]!;
  return (
    <label className="relative flex items-center gap-1.5 rounded-[9px] border border-border bg-surface py-2 pr-2.5 pl-3 hover:bg-[#fdfbfa]">
      <span className="text-[12.5px] font-medium text-fg-subtle">{label}:</span>
      <span className="text-[12.5px] font-semibold text-fg">{current[1]}</span>
      <DomainIcon name="filter-chevron" size={15} />
      <select
        aria-label={label}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

function Folders({
  data,
  active,
  onPick,
  canManage,
}: {
  data: DomainList;
  active: string;
  onPick: (folder: string) => void;
  canManage: boolean;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<null | { folder?: Folder }>(null);
  const [name, setName] = useState("");
  const toast = useToast();
  const save = useMutation({
    mutationFn: async () => {
      if (editing?.folder) await domainsApi.renameFolder(orgId, editing.folder.id, name.trim());
      else await domainsApi.createFolder(orgId, name.trim());
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      setEditing(null);
      toast.show({ tone: "success", title: "Folder saved" });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => domainsApi.deleteFolder(orgId, id),
    onSuccess: async (_, id) => {
      if (active === id) onPick("");
      await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
      setEditing(null);
      toast.show({ tone: "success", title: "Folder deleted" });
    },
  });
  const open = (folder?: Folder) => {
    setName(folder?.name ?? "");
    save.reset();
    setEditing({ folder });
  };

  return (
    <aside className="flex w-full shrink-0 flex-col gap-1 overflow-hidden rounded-[18px] border border-border bg-surface p-3.5 lg:w-[248px]">
      <div className="flex items-center pr-1 pb-1.5 pl-2">
        <p className="text-[11.5px] font-semibold tracking-[0.575px] text-fg-subtle uppercase">Folders</p>
        <span className="flex-1" />
        <Button
          unstyled
          requires="domains:manage"
          onClick={() => open()}
          aria-label="New folder"
          className="text-[12.5px] font-semibold text-brand-500 hover:underline"
        >
          + New
        </Button>
      </div>
      <FolderRow
        icon="layers"
        name="All domains"
        count={data.all_count}
        active={active === ""}
        onClick={() => onPick("")}
      />
      {data.folders.map((f) => (
        <FolderRow
          key={f.id}
          icon="folder"
          name={f.name}
          sub={`${f.domain_count} ${f.domain_count === 1 ? "domain" : "domains"}`}
          count={f.domain_count}
          active={active === f.id}
          onClick={() => onPick(f.id)}
          onEdit={canManage ? () => open(f) : undefined}
        />
      ))}
      <FolderRow
        icon="folder"
        name="Unfiled"
        sub="Not in a folder"
        count={data.unfiled_count}
        active={active === "unfiled"}
        onClick={() => onPick("unfiled")}
      />

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.folder ? "Rename folder" : "New folder"}
        subtitle="Group domains by brand, client or campaign"
        footer={
          <>
            {editing?.folder && (
              <Button
                variant="secondary"
                size="lg"
                loading={remove.isPending}
                onClick={() => remove.mutate(editing.folder!.id)}
              >
                Delete folder
              </Button>
            )}
            <Button variant="secondary" size="lg" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              size="lg"
              disabledReason={name.trim() ? undefined : "Enter a folder name"}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {editing?.folder ? "Save" : "Create folder"}
            </Button>
          </>
        }
        note={editing?.folder ? "Deleting a folder keeps its domains (they become unfiled)." : undefined}
      >
        <Field size="lg" label="Folder name">
          {({ id }) => (
            <Input
              size="lg"
              id={id}
              value={name}
              maxLength={60}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && save.mutate()}
              placeholder="Main brand"
            />
          )}
        </Field>
        <ErrorAlert error={save.error ?? remove.error} />
      </Modal>
    </aside>
  );
}

function FolderRow({
  icon,
  name,
  sub,
  count,
  active,
  onClick,
  onEdit,
}: {
  icon: string;
  name: string;
  sub?: string;
  count: number;
  active: boolean;
  onClick: () => void;
  onEdit?: () => void;
}) {
  return (
    <div
      className={cx("group flex items-center rounded-[10px]", active ? "bg-[#fef3f0]" : "hover:bg-[#faf6ef]")}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-[9px] text-left"
      >
        <DomainIcon name={icon} size={15} />
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className={cx("truncate text-[13.5px] font-semibold", active ? "text-brand-700" : "text-fg")}>
            {name}
          </span>
          {sub && !active && <span className="truncate text-[11.5px] text-fg-subtle">{sub}</span>}
        </span>
        <span className="text-[12.5px] font-semibold text-fg-subtle">{count}</span>
      </button>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${name}`}
          className="mr-1.5 hidden rounded px-1 text-[12px] font-semibold text-fg-subtle group-hover:block hover:text-fg"
        >
          Edit
        </button>
      )}
    </div>
  );
}

const COLS = "grid grid-cols-[minmax(170px,1fr)_100px_118px_86px_118px_128px_76px] items-center gap-0";

function DomainTable({ domains, folders }: { domains: Domain[]; folders: Folder[] }) {
  const folderName = useMemo(() => new Map(folders.map((f) => [f.id, f.name])), [folders]);
  return (
    // Focusable so keyboard users can scroll the wide table (axe: scrollable-region-focusable).
    <div
      className="overflow-x-auto rounded-[18px] border border-border bg-surface"
      role="region"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label="Domains table"
    >
      <div className="min-w-[840px]">
        <div className={cx(COLS, "px-[18px] pt-3 pb-2.5 text-[11px] font-semibold text-fg-subtle uppercase")}>
          <span>Domain</span>
          <span>Folder</span>
          <span>Expires</span>
          <span>Auto-renew</span>
          <span>Connected to</span>
          <span>Status</span>
          <span />
        </div>
        {domains.map((d) => (
          <DomainRow
            key={d.id}
            domain={d}
            folder={d.folder_id ? (folderName.get(d.folder_id) ?? "—") : "Unfiled"}
          />
        ))}
      </div>
    </div>
  );
}

function DomainRow({ domain: d, folder }: { domain: Domain; folder: string }) {
  const pill = healthPill(d.health);
  const pending =
    d.status === "registering" || d.status === "transferring_in" || d.status === "transferring_out";
  const connected = d.site_id
    ? d.connection === "connected"
      ? "Website"
      : "Website (setting up)"
    : "Nothing";
  const lockLabel =
    d.status === "active" || d.status === "transferring_out" ? (d.transfer_lock ? "Locked" : "Open") : "";
  const expiryNote =
    d.status === "expired" || d.status === "unrecoverable" ? "expired" : relativeDays(d.days_left);
  return (
    <>
      <div className="h-px bg-border" />
      <div className={cx(COLS, "px-[18px] py-3")}>
        <div className="flex min-w-0 flex-col gap-[3px] pr-3">
          <div className="flex min-w-0 items-center gap-1.5">
            <Link
              to={`/domains/${d.id}`}
              className="truncate text-[13.5px] font-semibold text-fg hover:underline"
            >
              {d.display_name}
            </Link>
            {d.is_primary && (
              <span className="shrink-0 rounded-md bg-[#f3ebe7] px-1.5 py-px text-[10.5px] font-semibold text-[#5c5551]">
                Primary
              </span>
            )}
          </div>
          {lockLabel && <span className="text-[11.5px] text-fg-subtle">{lockLabel}</span>}
        </div>
        <span className="truncate pr-2 text-[12.5px] text-[#5c5551]">{folder}</span>
        <div className="flex flex-col gap-px whitespace-nowrap">
          <span className="text-[12.5px] text-fg">{longDate(d.expires_at)}</span>
          {expiryNote && <span className="text-[11.5px] font-semibold text-fg-subtle">{expiryNote}</span>}
        </div>
        <span className="text-[12.5px] text-[#5c5551]">{d.auto_renew ? "On" : "Off"}</span>
        <span className="truncate pr-2 text-[12.5px] text-[#5c5551]">{connected}</span>
        <span>
          <Badge tone={d.status === "transferring_in" ? "info" : pill.tone} size="sm">
            {d.status === "transferring_in" ? "Pending transfer" : pill.label}
          </Badge>
        </span>
        <span className="text-right">
          <Link
            to={`/domains/${d.id}`}
            className="text-[12.5px] font-semibold whitespace-nowrap text-brand-500 hover:underline"
          >
            {pending ? "Track →" : "Manage →"}
          </Link>
        </span>
      </div>
    </>
  );
}

function ExternalTable({ rows }: { rows: ExternalDomain[] }) {
  const tone = (s: ExternalDomain["status"]) =>
    s === "active"
      ? (["Connected", "success"] as const)
      : s === "failed"
        ? (["Needs fix", "danger"] as const)
        : (["Waiting on DNS", "warning"] as const);
  return (
    <div className="mt-2 flex flex-col gap-2.5">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <p className="text-[15px] font-semibold text-fg">Registered elsewhere</p>
          <p className="text-[12.5px] text-fg-subtle">
            Domains you own at another registrar, pointed at your sites
          </p>
        </div>
        <Link
          to="/domains/connect-external"
          className="text-[12.5px] font-semibold text-brand-500 hover:underline"
        >
          Connect another →
        </Link>
      </div>
      <div className="overflow-hidden rounded-[18px] border border-border bg-surface">
        {rows.map((r, i) => {
          const [label, t] = tone(r.status);
          const link =
            r.site.kind === "external" ? `/hosting/sites/${r.site.id}` : `/websites/${r.site.id}?tab=domains`;
          return (
            <div
              key={r.id}
              className={cx("flex items-center gap-3 px-[18px] py-3", i > 0 && "border-t border-border")}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold text-fg">{r.hostname}</p>
                <p className="text-[11.5px] text-fg-subtle">Points to {r.site.name}</p>
              </div>
              <Badge tone={t} size="sm">
                {label}
              </Badge>
              <Link
                to={
                  r.status === "active"
                    ? link
                    : `/domains/connect-external?hostname=${encodeURIComponent(r.hostname)}&site=${r.site.id}`
                }
                className="w-[76px] text-right text-[12.5px] font-semibold text-brand-500 hover:underline"
              >
                {r.status === "active" ? "Manage →" : "Fix →"}
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyList({ filtered, hasExternal }: { filtered: boolean; hasExternal: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[18px] border border-border bg-surface px-6 py-14 text-center">
      <span className="flex size-11 items-center justify-center rounded-xl bg-[#faf6ef]">
        <DomainIcon name="globe" size={24} />
      </span>
      {filtered ? (
        <>
          <p className="text-base font-semibold text-fg">No domains match these filters</p>
          <Link to="/domains" className="text-sm font-semibold text-brand-500 hover:underline">
            Clear filters
          </Link>
        </>
      ) : (
        <>
          <p className="text-base font-semibold text-fg">
            {hasExternal ? "No domains registered with CortexGrip yet" : "Give your website its own address"}
          </p>
          <p className="max-w-md text-sm text-fg-subtle">
            Your website already has a free CortexGrip address. Find a domain like yourbusiness.com and we'll
            register it, set up DNS and connect it for you — or connect one you already own.
          </p>
          <div className="mt-1 flex flex-wrap justify-center gap-2.5">
            <LinkButton to="/domains/find" className={buttonClass("brand", "pill")} requires="domains:manage">
              + Find a domain
            </LinkButton>
            <Link
              to="/domains/connect-external"
              className="inline-flex items-center rounded-full border border-border px-4 py-[9px] text-[13px] font-semibold text-fg hover:bg-muted"
            >
              Connect a domain I own
            </Link>
            <LinkButton
              to="/domains/transfer/bulk"
              className="inline-flex items-center rounded-full border border-border px-4 py-[9px] text-[13px] font-semibold text-fg hover:bg-muted"
              requires="domains:manage"
            >
              Transfer domains I own
            </LinkButton>
          </div>
        </>
      )}
    </div>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const shown: (number | "…")[] = [];
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 1 || (page <= 3 && p <= 3)) shown.push(p);
    else if (shown.at(-1) !== "…") shown.push("…");
  }
  const box = "flex size-[30px] items-center justify-center rounded-lg text-[12.5px] font-semibold";
  return (
    <div className="flex items-center gap-2.5 pt-3.5">
      <p className="flex-1 text-[12.5px] text-fg-subtle">
        Showing {from}–{to} of {total}
      </p>
      {pages > 1 && (
        <div className="flex items-center gap-1.5">
          <PageButton
            className={box}
            disabledReason={page <= 1 ? "You're on the first page" : undefined}
            onClick={() => onPage(page - 1)}
            label="Previous page"
          >
            ‹
          </PageButton>
          {shown.map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className="text-[12.5px] text-fg-subtle">
                ...
              </span>
            ) : (
              <PageButton
                key={p}
                className={cx(box, p === page && "border-brand-500 bg-brand-500 text-white")}
                onClick={() => onPage(p)}
                label={`Page ${p}`}
                current={p === page}
              >
                {p}
              </PageButton>
            ),
          )}
          <PageButton
            className={box}
            disabledReason={page >= pages ? "You're on the last page" : undefined}
            onClick={() => onPage(page + 1)}
            label="Next page"
          >
            ›
          </PageButton>
        </div>
      )}
    </div>
  );
}

function PageButton({
  className,
  children,
  onClick,
  disabledReason,
  label,
  current,
}: {
  className: string;
  children: ReactNode;
  onClick: () => void;
  disabledReason?: string;
  label: string;
  current?: boolean;
}) {
  return (
    <Button
      unstyled
      onClick={onClick}
      disabledReason={disabledReason}
      aria-label={label}
      aria-current={current ? "page" : undefined}
      className={cx(className, !current && "border border-border text-[#5c5551] hover:bg-muted")}
    >
      {children}
    </Button>
  );
}
